import React from "react";
import { Box, Button, Tab, Tabs, Alert, Tooltip } from "@mui/material";

// Interfaz para las reservas existentes
import { Reservation } from "../reservations/types/Reservation";
import TabInformacion from "./TabInformacion";
import TabHuespedes from "./TabHuespedes";
import TabEstacionamiento from "./TabEstacionamiento";
import { CalculationService } from "../../services/calculationService";
import { useNewReservationForm } from "./hooks/useNewReservationForm";
import BlacklistAlert from "../shared/components/BlacklistAlert";

interface NewReservationFormProps {
  onClose: () => void;
  onSuccess: (reservation: Reservation) => void;
  existingReservations?: Reservation[];
  initialData?: Reservation;
  soloEstacionamiento?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0, minHeight: 0 }}>{children}</Box>}
    </div>
  );
}

export const NewReservationForm = ({
  onClose,
  onSuccess,
  existingReservations = [],
  initialData,
  soloEstacionamiento = false,
}: NewReservationFormProps) => {
  const {
    formData,
    setFormData,
    errors,
    documentoWarning,
    setErrors,
    savePending,
    handleSubmit,
    handleTextChange,
    handleSelectChange,
    handleDateRangeClick,
    dateRangeAnchorEl,
    handleDateRangeClose,
    handleDateRangeChange,
    dateRange,
    zonas,
    categoriaDepartamento,
    errorMsg,
    overlapError,
    activeTab,
    handleTabChange,
    loadingZonas,
    handleClienteSelect,
    handleClienteClear,
    hasSelectedCliente,
    esEmpresa,
    handleEsEmpresaChange,
    esBooking,
    handleEsBookingChange,
    esAirbnb,
    handleEsAirbnbChange,
    continueWithReservation,
    handleInputChange,
  } = useNewReservationForm({
    onClose,
    onSuccess,
    existingReservations,
    initialData,
    soloEstacionamiento,
  });

  const openDateRange = Boolean(dateRangeAnchorEl);
  const guestInfoMissing =
    esEmpresa &&
    !esBooking &&
    !esAirbnb &&
    !formData.afiliado_nombre?.trim() &&
    !formData.afiliado_apellido?.trim() &&
    !formData.afiliado_dni?.trim();

  const huespedesTabLabel = (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
    >
      Huéspedes
      {guestInfoMissing && (
        <Tooltip title="Falta huésped o afiliado" arrow>
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "error.main",
              display: "inline-block",
            }}
          />
        </Tooltip>
      )}
    </Box>
  );

  // Modo completo para nueva reserva
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {errorMsg && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <Alert severity="error">{errorMsg}</Alert>
        </Box>
      )}

      {overlapError && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
            {overlapError}
          </Alert>
        </Box>
      )}

      {errors.blacklist_warning && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <BlacklistAlert
            blacklistData={errors.blacklist_warning}
            onContinue={continueWithReservation}
            onCancel={onClose}
          />
        </Box>
      )}

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          mb: { xs: 1, sm: 1 },
          minHeight: { xs: 40, sm: 42 },
          flexShrink: 0,
          "& .MuiTabs-indicator": {
            display: "none",
          },
          "& .MuiTab-root.Mui-selected": {
            color: "primary.main",
            fontWeight: 600,
          },
          "& .MuiTab-root": {
            minHeight: { xs: 40, sm: 42 },
            padding: { xs: "6px 14px", sm: "7px 16px" },
            fontSize: { xs: "0.82rem", sm: "0.875rem" },
          },
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {!soloEstacionamiento && <Tab label="Información" />}
        {!soloEstacionamiento && <Tab label={huespedesTabLabel} />}
        {soloEstacionamiento && <Tab label="Estacionamiento" />}
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pt: 0.75, pr: { sm: 0.5 }, pb: 0.5 }}>
        {!soloEstacionamiento && (
          <TabPanel value={activeTab} index={0}>
            <TabInformacion
              formData={formData}
              setFormData={setFormData}
              zonas={zonas}
              errors={errors}
              documentoWarning={documentoWarning}
              categoriaDepartamento={categoriaDepartamento}
              calcularNoches={() => {
                const entrada = formData.entrada || new Date();
                const salida = formData.salida || new Date();
                return CalculationService.calcularNoches(entrada, salida);
              }}
              handleDateRangeClick={handleDateRangeClick}
              openDateRange={openDateRange}
              dateRangeAnchorEl={dateRangeAnchorEl || null}
              handleDateRangeClose={handleDateRangeClose}
              handleDateRangeChange={handleDateRangeChange}
              dateRange={dateRange}
              handleTextChange={handleTextChange}
              handleSelectChange={handleSelectChange}
              loadingZonas={loadingZonas}
              handleClienteSelect={handleClienteSelect}
              handleClienteClear={handleClienteClear}
              hasSelectedCliente={hasSelectedCliente}
              esEmpresa={esEmpresa}
              handleEsEmpresaChange={handleEsEmpresaChange}
              esBooking={esBooking}
              handleEsBookingChange={handleEsBookingChange}
              esAirbnb={esAirbnb}
              handleEsAirbnbChange={handleEsAirbnbChange}
            />
          </TabPanel>
        )}

        {!soloEstacionamiento && (
          <TabPanel value={activeTab} index={1}>
            <TabHuespedes
              formData={formData}
              setFormData={setFormData}
              setErrors={setErrors}
              guestInfoMissing={guestInfoMissing}
            />
          </TabPanel>
        )}

        {soloEstacionamiento && (
          <TabPanel value={activeTab} index={0}>
            <TabEstacionamiento
              formData={formData}
              errors={errors}
              soloEstacionamiento={soloEstacionamiento}
              handleInputChange={handleInputChange} // Pasar la función correcta
              handleDateRangeClick={handleDateRangeClick}
              openDateRange={openDateRange}
              dateRangeAnchorEl={dateRangeAnchorEl || null}
              handleDateRangeClose={handleDateRangeClose}
              handleDateRangeChange={handleDateRangeChange}
              dateRange={dateRange}
              calcularNoches={() => {
                const entrada = formData.entrada || new Date();
                const salida = formData.salida || new Date();
                return CalculationService.calcularNoches(entrada, salida);
              }}
            />
          </TabPanel>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          mt: { xs: 1, sm: 1.25 },
          justifyContent: "flex-end",
          flexShrink: 0,
          pt: 1,
          pb: 1,
          px: 0.25,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={savePending || !!overlapError}
          size="small"
          sx={{ py: { xs: 0.45, sm: 0.35 }, px: 2.5 }}
        >
          {savePending ? "Guardando..." : "Guardar"}
        </Button>
        <Button
          variant="text"
          color="primary"
          onClick={onClose}
          size="small"
          sx={{ py: { xs: 0.45, sm: 0.35 }, px: 2.5 }}
        >
          Cancelar
        </Button>
      </Box>
    </Box>
  );
};

export default NewReservationForm;
